import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CanvasBgFromVideo from "@/components/ui/canvas-bg-from-video";
import classNames from "classnames";
import RoundActionButton from "@/components/ui/buttons/round-action-button";
import AppBtn from "@/components/ui/buttons/app-btn";
import GenericSpinner from "@/components/ui/generic-spinner";
import { CartItem, HighlightInterface, ProductItemInterface } from "@/assets/interfaces";
import HighlightEndNotice from "./highlight-end-notice";
import { useModals } from "@/components/hooks/useModals";
import { toast } from "@/components/ui/toast";
import AddSingleItemToCart from "./add-single-item-to-cart";
import { computeItemAvailability } from "@/assets/js/utils/utils";

interface HighlightPlayerProps {
  highlight: HighlightInterface;
  isActive: boolean;
  goToNextHighlight: VoidFunction;
  goToPrevHighlight: VoidFunction;
  isFirstHighlight: boolean;
  isLastHighlight: boolean;
  playVideosMuted: boolean;
  setPlayVideosMuted: (muted: boolean) => void;
  toggleModal: (modal: string) => void;
  setCurrentVideoIndex: (index: number) => void;
  currentVideoIndex: number;
  cart: CartItem[];
  openCart: () => void;
  hasMoreHighlights?: boolean;
  isLoadingNewHighlights?: boolean;
  fetchNewHighlights?: () => void;
  hide: boolean;
  goToStore: () => void;
  storeName: string;
  openItem: (item: ProductItemInterface) => void;
  formatAsCurrency: (amount: number) => string;
  addOrRemoveItem?: (item: ProductItemInterface, variants: string[], quantities: number[]) => void;
  setQuantity?: (itemId: string, qty: number) => void;
  toggleCartModal?: (modal: string) => void;
  setCurrentProduct: (item: ProductItemInterface) => void;
}

const HighlightPlayer = ({
  highlight,
  isActive,
  goToNextHighlight,
  goToPrevHighlight,
  isFirstHighlight,
  isLastHighlight,
  playVideosMuted,
  setPlayVideosMuted,
  toggleModal,
  setCurrentVideoIndex,
  currentVideoIndex,
  cart,
  hasMoreHighlights = false,
  openCart,
  isLoadingNewHighlights = false,
  fetchNewHighlights,
  hide = false,
  goToStore,
  storeName,
  openItem,
  formatAsCurrency,
  addOrRemoveItem,
  setQuantity,
  setCurrentProduct,
  toggleCartModal,
}: HighlightPlayerProps) => {
  const videoRef = useRef<HTMLVideoElement[]>([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isDropUpOpen, setIsDropUpOpen] = useState<Boolean>(false);

  const [showEndNotice, setShowEndNotice] = useState(false);

  const getFirstItem = useCallback(
    (videoIndex: number): ProductItemInterface | undefined => {
      return highlight?.videos?.[videoIndex]?.products?.[0];
    },
    [highlight, currentVideoIndex]
  );

  const oneOrMoreItemsInCart = useMemo(() => {
    if (!isActive) return false;
    const allItemIds = highlight.videos[currentVideoIndex].products.map((p) => p.id);
    return allItemIds.some((id) => cart.some((cartItem) => cartItem.item_id === id));
  }, [cart, currentVideoIndex, isActive]);

  useEffect(() => {
    if (isActive && videoRef.current) {
      videoRef.current[currentVideoIndex].play();
      setProgress(0);
      setIsLoading(true);
    }

    if (isDropUpOpen) {
      setIsDropUpOpen(false);
    }
  }, [isActive, currentVideoIndex]);

  useEffect(() => {
    const video = videoRef.current[currentVideoIndex];
    if (!video) return;

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => setIsPlaying(false); // optional

    video.addEventListener("play", handlePlay);
    video.addEventListener("pause", handlePause);
    video.addEventListener("ended", handleEnded);

    return () => {
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("pause", handlePause);
      video.removeEventListener("ended", handleEnded);
    };
  }, [isActive]);

  useEffect(() => {
    if (fetchNewHighlights) {
      setTimeout(() => {
        setShowEndNotice(false);
      }, 250);
    }
  }, [fetchNewHighlights]);

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const progress =
        (videoRef.current[currentVideoIndex].currentTime / videoRef.current[currentVideoIndex].duration) * 100;
      setProgress(progress);
    }
  };

  const handleVideoClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const width = rect.width;

    if (x > width * 0.66) {
      handleNext();
    } else if (x < width * 0.33) {
      handlePrevious();
    } else {
      togglePlay();
    }
  };

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current[currentVideoIndex].pause();
      } else {
        videoRef.current[currentVideoIndex].play();
      }
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current[currentVideoIndex].muted = !playVideosMuted;
      setPlayVideosMuted(!playVideosMuted);
    }
  };

  const handleNext = () => {
    setIsDropUpOpen(false);
    if (currentVideoIndex === highlight.videos.length - 1) {
      if (!isLastHighlight) {
        setCurrentVideoIndex(0);
        goToNextHighlight();
      } else {
        setShowEndNotice(true);
      }
      //what should we do when it's the last highlight
    } else {
      setCurrentVideoIndex(currentVideoIndex + 1);
    }
  };

  const handlePrevious = () => {
    setIsDropUpOpen(false);
    if (currentVideoIndex === 0) {
      if (!isFirstHighlight) {
        setCurrentVideoIndex(0);
        goToPrevHighlight();
      }
      //what should we do when it's the first highlight
    } else {
      setCurrentVideoIndex(currentVideoIndex - 1);
    }
  };

  const handleVideoEnd = () => {
    handleNext();
  };

  const openModal = (modal: string) => {
    if (videoRef.current[currentVideoIndex]) {
      videoRef.current[currentVideoIndex].pause();
    }

    toggleModal(modal);
  };

  const addSingleItemToCart = (item: ProductItemInterface, quantity: number) => {
    addOrRemoveItem(item, [], [quantity]);
    setIsDropUpOpen(false);
  };

  const toggleDropUp = () => {
    setIsDropUpOpen(!isDropUpOpen);
  };

  const selectItems = () => {
    if (highlight.videos[currentVideoIndex] && highlight.videos[currentVideoIndex].products.length > 1) {
      openModal("products");
    } else {
      var item = getFirstItem(currentVideoIndex);
      const itemHasVariants = item?.variants?.options.length > 0;

      if (itemHasVariants) {
        setCurrentProduct(item);
        toggleCartModal("variants");
        if (isPlaying) {
          togglePlay();
        }
      } else {
        toggleDropUp();
        if (isPlaying) {
          togglePlay();
        }
      }
    }
  };

  const replayHighlight = () => {
    setCurrentVideoIndex(0);
    setShowEndNotice(false);
  };

  const shareHighlight = () => {
    const allProducts = highlight.videos.flatMap((v) => v.products);
    const baseUrl = window.location.origin;
    const url = `${baseUrl}/highlights/${highlight.slug}`;
    const text = `${highlight.title} - ${highlight.videos.length} videos | ${allProducts[0].name}${
      allProducts.length > 1 ? ` and ${allProducts.length - 1} more` : ""
    }`;
    const image = `${baseUrl}/api/square-image?url=${encodeURIComponent(highlight.videos[0].thumbnail)}`;

    const shareData = { url, text, image };

    if (navigator.share) {
      navigator.share(shareData);
    } else {
      navigator.clipboard.writeText(url);
      toast.success({ title: "Link copied to clipboard", message: "Share the highlight with your friends" });
    }
  };

  if (hide) {
    return null;
  }

  if (!isActive) {
    return (
      <figure className="h-full w-full relative">
        <img src={highlight.videos[0].thumbnail} alt={highlight.title} className="w-full h-full object-cover" />
        <div
          className="absolute inset-0 bg-black/70 flex items-center justify-center"
          style={{ backdropFilter: "blur(2px)" }}
        >
          <h2 className="text-white text-3xl font-bold">{highlight.title}</h2>
        </div>
      </figure>
    );
  }

  return (
    <figure
      className="h-full w-full relative"
      style={{
        backgroundImage: `url(${highlight?.videos[currentVideoIndex]?.thumbnail})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
      onClick={handleVideoClick}
    >
      <div
        className="absolute inset-0 bg-black/10 flex items-center justify-center z-[1]"
        style={{ backdropFilter: "blur(7px)" }}
      ></div>
      <div className="absolute top-4 left-4 right-4 z-[20]" onClick={(e) => e.stopPropagation()}>
        <div className="flex gap-1">
          {highlight.videos.map((_, index) => (
            <div key={index} className="h-1 flex-1 bg-white bg-opacity-30 rounded-full overflow-hidden">
              <div
                className={classNames("h-full bg-white transition-all duration-100", {
                  "w-full": index === currentVideoIndex && progress === 100,
                  "w-0": index > currentVideoIndex,
                })}
                style={{
                  width: index === currentVideoIndex ? `${progress}%` : index < currentVideoIndex ? "100%" : "0%",
                }}
              />
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between mt-2.5">
          <h6 className="text-lg sm:text-xl font-display font-semibold text-white">{highlight.title}</h6>

          <div className="flex items-center">
            <RoundActionButton onClick={() => togglePlay()} className="bg-transparent !text-white">
              <figure className="w-5">{isPlaying ? icons.pause : icons.play}</figure>
            </RoundActionButton>
            <RoundActionButton onClick={() => toggleMute()} className="bg-transparent !text-white">
              <figure className="w-5">{playVideosMuted ? icons.volumeOff : icons.volumeOn}</figure>
            </RoundActionButton>
            <RoundActionButton onClick={shareHighlight} className="bg-transparent !text-white">
              {/* prettier-ignore */}
              <svg width="18" viewBox="0 0 24 24" fill="none">
                <path d="M13 11L21.2 2.80005" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M22 6.8V2H17.2" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M11 2H9C4 2 2 4 2 9V15C2 20 4 22 9 22H15C20 22 22 20 22 15V13" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </RoundActionButton>
          </div>
        </div>
      </div>
      <div className="h-full w-full relative z-[10] flex items-center justify-center">
        <video
          ref={(el) => (videoRef.current[currentVideoIndex] = el)}
          src={highlight.videos[currentVideoIndex].url}
          className="w-full max-h-full relative z-[10]"
          onTimeUpdate={handleTimeUpdate}
          onEnded={handleVideoEnd}
          muted={playVideosMuted}
          autoPlay
          playsInline
          onLoadedData={() => setIsLoading(false)}
          onError={() => setIsLoading(false)}
        />
      </div>

      {highlight && highlight?.videos[currentVideoIndex]?.products?.length == 1 && (
        <AddSingleItemToCart
          isDropUpOpen={isDropUpOpen}
          formatAsCurrency={formatAsCurrency}
          openItem={openItem}
          setIsDropUpOpen={setIsDropUpOpen}
          item={computeItemAvailability(highlight?.videos[currentVideoIndex]?.products[0])}
          cart={cart}
          saveToCart={addSingleItemToCart}
        />
      )}
      <div
        className="flex items-center w-full absolute left-0 bottom-[20px] px-4.5 gap-x-3 z-[20]"
        onClick={(e) => e.stopPropagation()}
      >
        <AppBtn color="primary" size="lg" className="flex-1" onClick={selectItems}>
          {oneOrMoreItemsInCart ? "Update Cart" : "Add To Cart"}
        </AppBtn>
        <AppBtn
          color="neutral"
          size="lg"
          onClick={openCart}
          className=" rounded-10 text-primary-500 hover:bg-opacity-[1!important] relative"
        >
          {/* prettier-ignore */}
          <svg width="27" height="27" viewBox="0 0 27 27" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3.9165 7L7.1665 2.66667H20.1665L23.4165 7M3.9165 7V22.1667C3.9165 22.7413 4.14478 23.2924 4.55111 23.6987C4.95743 24.1051 5.50853 24.3333 6.08317 24.3333H21.2498C21.8245 24.3333 22.3756 24.1051 22.7819 23.6987C23.1882 23.2924 23.4165 22.7413 23.4165 22.1667V7M3.9165 7H23.4165M17.9998 11.3333C17.9998 12.4826 17.5433 13.5848 16.7306 14.3975C15.918 15.2101 14.8158 15.6667 13.6665 15.6667C12.5172 15.6667 11.415 15.2101 10.6024 14.3975C9.78972 13.5848 9.33317 12.4826 9.33317 11.3333" stroke="#3E3E3E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <div className="absolute top-1.25 right-2 sm:top-1.5 sm:right-3.5 h-4 w-4 sm:h-4.5 sm:w-4.5 rounded-full bg-primary-500 text-white text-[10px] font-bold flex items-center justify-center">
            <span className="inline-block">{cart && cart.length > 0 ? cart.length : 0}</span>
          </div>
        </AppBtn>
      </div>
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center z-[5]">
          <GenericSpinner width={24} />
        </div>
      )}
      <HighlightEndNotice
        cart={cart}
        show={showEndNotice}
        toggle={setShowEndNotice}
        hasMoreHighlights={hasMoreHighlights}
        replayHighlight={replayHighlight}
        goToStore={goToStore}
        loadMoreHighlights={fetchNewHighlights}
        openCart={openCart}
        showAllProducts={() => toggleModal("all_products")}
        isLoadingNewHighlights={isLoadingNewHighlights}
      />
    </figure>
  );
};

export default HighlightPlayer;

const icons = {
  volumeOff:
    /* prettier-ignore */
    <svg width="100%" viewBox="0 0 24 24" fill="none">
      <path d="M18.0003 16.7503C17.8403 16.7503 17.6903 16.7003 17.5503 16.6003C17.2203 16.3503 17.1503 15.8803 17.4003 15.5503C18.6603 13.8703 18.9303 11.6403 18.1203 9.71032C17.9603 9.33032 18.1403 8.89032 18.5203 8.73032C18.9003 8.57032 19.3403 8.75032 19.5003 9.13032C20.5203 11.5503 20.1703 14.3603 18.6003 16.4603C18.4503 16.6503 18.2303 16.7503 18.0003 16.7503Z" fill="currentColor"/>
      <path d="M19.8284 19.2492C19.6684 19.2492 19.5184 19.1992 19.3784 19.0992C19.0484 18.8492 18.9784 18.3792 19.2284 18.0492C21.3684 15.1992 21.8384 11.3792 20.4584 8.08922C20.2984 7.70922 20.4784 7.26922 20.8584 7.10922C21.2384 6.94922 21.6784 7.12922 21.8384 7.50922C23.4284 11.2892 22.8884 15.6692 20.4284 18.9492C20.2884 19.1492 20.0584 19.2492 19.8284 19.2492Z" fill="currentColor"/>
      <path d="M14.0405 12.9581C14.6705 12.3281 15.7505 12.7781 15.7505 13.6681V16.5981C15.7505 18.3181 15.1305 19.6081 14.0205 20.2281C13.5705 20.4781 13.0705 20.5981 12.5505 20.5981C11.7505 20.5981 10.8905 20.3281 10.0105 19.7781L9.37047 19.3781C8.83047 19.0381 8.74047 18.2781 9.19047 17.8281L14.0405 12.9581Z" fill="currentColor"/>
      <path d="M21.77 2.22891C21.47 1.92891 20.98 1.92891 20.68 2.22891L15.73 7.17891C15.67 5.57891 15.07 4.37891 14.01 3.78891C12.89 3.16891 11.46 3.32891 10 4.23891L7.09 6.05891C6.89 6.17891 6.66 6.24891 6.43 6.24891H5.5H5C2.58 6.24891 1.25 7.57891 1.25 9.99891V13.9989C1.25 16.4189 2.58 17.7489 5 17.7489H5.16L2.22 20.6889C1.92 20.9889 1.92 21.4789 2.22 21.7789C2.38 21.9189 2.57 21.9989 2.77 21.9989C2.97 21.9989 3.16 21.9189 3.31 21.7689L21.77 3.30891C22.08 3.00891 22.08 2.52891 21.77 2.22891Z" fill="currentColor"/>
    </svg>,
  volumeOn:
    /* prettier-ignore */
    <svg width="100%" viewBox="0 0 24 24" fill="none">
      <path d="M18.0003 16.7503C17.8403 16.7503 17.6903 16.7003 17.5503 16.6003C17.2203 16.3503 17.1503 15.8803 17.4003 15.5503C18.9703 13.4603 18.9703 10.5403 17.4003 8.45027C17.1503 8.12027 17.2203 7.65027 17.5503 7.40027C17.8803 7.15027 18.3503 7.22027 18.6003 7.55027C20.5603 10.1703 20.5603 13.8303 18.6003 16.4503C18.4503 16.6503 18.2303 16.7503 18.0003 16.7503Z" fill="currentColor"/>
      <path d="M19.8284 19.2503C19.6684 19.2503 19.5184 19.2003 19.3784 19.1003C19.0484 18.8503 18.9784 18.3803 19.2284 18.0503C21.8984 14.4903 21.8984 9.51027 19.2284 5.95027C18.9784 5.62027 19.0484 5.15027 19.3784 4.90027C19.7084 4.65027 20.1784 4.72027 20.4284 5.05027C23.4984 9.14027 23.4984 14.8603 20.4284 18.9503C20.2884 19.1503 20.0584 19.2503 19.8284 19.2503Z" fill="currentColor"/>
      <path d="M14.02 3.78168C12.9 3.16168 11.47 3.32168 10.01 4.23168L7.09 6.06168C6.89 6.18168 6.66 6.25168 6.43 6.25168H5.5H5C2.58 6.25168 1.25 7.58168 1.25 10.0017V14.0017C1.25 16.4217 2.58 17.7517 5 17.7517H5.5H6.43C6.66 17.7517 6.89 17.8217 7.09 17.9417L10.01 19.7717C10.89 20.3217 11.75 20.5917 12.55 20.5917C13.07 20.5917 13.57 20.4717 14.02 20.2217C15.13 19.6017 15.75 18.3117 15.75 16.5917V7.41168C15.75 5.69168 15.13 4.40168 14.02 3.78168Z" fill="currentColor"/>
    </svg>,
  play:
    /* prettier-ignore */
    <svg width="100%" viewBox="0 0 24 24" fill="none">
      <path d="M11.9688 2C6.44875 2 1.96875 6.48 1.96875 12C1.96875 17.52 6.44875 22 11.9688 22C17.4888 22 21.9688 17.52 21.9688 12C21.9688 6.48 17.4988 2 11.9688 2ZM14.9688 14.23L12.0687 15.9C11.7087 16.11 11.3088 16.21 10.9187 16.21C10.5188 16.21 10.1287 16.11 9.76875 15.9C9.04875 15.48 8.61875 14.74 8.61875 13.9V10.55C8.61875 9.72 9.04875 8.97 9.76875 8.55C10.4888 8.13 11.3487 8.13 12.0787 8.55L14.9787 10.22C15.6987 10.64 16.1287 11.38 16.1287 12.22C16.1287 13.06 15.6987 13.81 14.9688 14.23Z" fill="currentColor"/>
    </svg>,
  pause:
    /* prettier-ignore */
    <svg width="100%" viewBox="0 0 24 24" fill="none">
      <path d="M10.65 19.11V4.89C10.65 3.54 10.08 3 8.64 3H5.01C3.57 3 3 3.54 3 4.89V19.11C3 20.46 3.57 21 5.01 21H8.64C10.08 21 10.65 20.46 10.65 19.11Z" fill="currentColor"/>
      <path d="M21.0016 19.11V4.89C21.0016 3.54 20.4316 3 18.9916 3H15.3616C13.9316 3 13.3516 3.54 13.3516 4.89V19.11C13.3516 20.46 13.9216 21 15.3616 21H18.9916C20.4316 21 21.0016 20.46 21.0016 19.11Z" fill="currentColor"/>
    </svg>,
};
